import { createRouter, createWebHistory } from 'vue-router';
import { moduleRouteName } from '@dashboard/module-contract/ui';
import { BUNDLED } from './modules/registry';
import { modulePath } from './modules/nav';
import { ensureResolved, hasRole, isSignedIn } from './session';
import type { Router, RouteRecordSingleView } from 'vue-router';
import type { BundledModule } from './modules/registry-types';

/**
 * The core owns the router. Modules contribute descriptors — a path, a title,
 * a role and a lazily-imported component — and never touch it directly, which
 * is what keeps `./ui` free of any Vue dependency.
 */

declare module 'vue-router' {
	interface RouteMeta {
		/** Reachable without a session. Only sign-in is. */
		readonly anonymous?: boolean;
		/** Checked here for the user's benefit; enforced by the API regardless. */
		readonly requiredRole?: string;
		/**
		 * Which module's page this is. Absent on the core's own. Read by
		 * `createViewer` to scope what a view may ask about to the roles that
		 * module could have declared.
		 */
		readonly moduleId?: string;
		/**
		 * What this page is called, for the browser tab (`D121`). Module
		 * routes have carried one all along; the core's own did not, so every
		 * core page would have shared one title.
		 */
		readonly title?: string;
	}
}

/**
 * `RouteRecordSingleView` rather than `RouteRecordRaw`: the latter is a union
 * that includes a redirect record, and an object literal missing `redirect`
 * gets matched against that member and reported as missing it.
 */
export function moduleRoutes(modules: readonly BundledModule[] = BUNDLED): RouteRecordSingleView[] {
	return modules.flatMap(({ manifest, ui }) =>
		ui.routes.map((route) => ({
			path: modulePath(manifest.id, route.path),
			name: moduleRouteName(manifest.id, route.path),
			// The contract types a view as `() => Promise<unknown>` so it can
			// stay Vue-free; the router is where it becomes a component again.
			component: route.component as RouteRecordSingleView['component'],
			meta: { moduleId: manifest.id, requiredRole: route.requiredRole, title: route.title },
		})),
	);
}

export function createAppRouter(modules: readonly BundledModule[] = BUNDLED): Router {
	const router = createRouter({
		history: createWebHistory(),
		routes: [
			{
				path: '/sign-in',
				name: 'sign-in',
				component: () => import('./views/sign-in.vue'),
				meta: { anonymous: true, title: 'Sign in' },
			},
			{
				path: '/',
				name: 'home',
				component: () => import('./views/home.vue'),
				// No title: the home page's title is the deployment's name alone.
				meta: {},
			},

			/**
			 * Reached from the user menu rather than the left nav: it is about
			 * the person, not about the client's AWS account. No `requiredRole`
			 * — anyone signed in has one of these.
			 */
			{
				path: '/profile',
				name: 'profile',
				component: () => import('./views/profile.vue'),
				meta: { title: 'Profile' },
			},

			/**
			 * The core's own admin pages. `requiredRole` is checked by the
			 * guard for the user's benefit; the API checks it again on every
			 * request, and that one is the control.
			 */
			{
				path: '/admin/users',
				name: 'admin-users',
				component: () => import('./views/admin/users.vue'),
				meta: { requiredRole: 'core:admin', title: 'Users' },
			},
			{
				path: '/admin/settings',
				name: 'admin-settings',
				component: () => import('./views/admin/settings.vue'),
				meta: { requiredRole: 'core:admin', title: 'Settings' },
			},
			{
				path: '/admin/security',
				name: 'admin-security',
				component: () => import('./views/admin/security.vue'),
				meta: { requiredRole: 'core:admin', title: 'Security' },
			},
			{
				path: '/admin/audit',
				name: 'admin-audit',
				component: () => import('./views/admin/audit.vue'),
				meta: { requiredRole: 'core:admin', title: 'Audit log' },
			},
			{
				path: '/forbidden',
				name: 'forbidden',
				component: () => import('./views/forbidden.vue'),
				meta: { title: 'Not allowed' },
			},
			...moduleRoutes(modules),
			{
				path: '/:pathMatch(.*)*',
				name: 'not-found',
				component: () => import('./views/not-found.vue'),
				meta: { title: 'Not found' },
			},
		],
	});

	router.beforeEach(async (to) => {
		// Every navigation waits on one `/api/me`, shared between concurrent
		// callers. Without it the first render has to guess, and guessing
		// anonymous flashes the sign-in form at someone who is signed in.
		await ensureResolved();

		if (to.meta.anonymous === true) {
			/**
			 * **A stale session is signed in and still needs this page.** `012`
			 * says the caller is who they say they are and merely needs a newer
			 * session, so `isSignedIn` stays true — and this guard sent them
			 * home, which made the "Sign in again" link on every stale notice
			 * in the app do nothing at all.
			 *
			 * It worked after a role change only because that *revokes* the
			 * session, and a revoked one is not signed in.
			 */
			return isSignedIn.value ? { name: 'home' } : true;
		}

		if (!isSignedIn.value) {
			return { name: 'sign-in', query: { next: to.fullPath } };
		}

		if (to.meta.requiredRole !== undefined && !hasRole(to.meta.requiredRole)) {
			// Not a redirect to sign-in: they are signed in, and signing in
			// again would not help. Saying so is more use than a loop.
			return { name: 'forbidden' };
		}

		return true;
	});

	return router;
}
